import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TextField, Box } from '@mui/material';

/**
 * DigitInput - Individual digit input component with auto-focus and validation
 * Handles single digit input with automatic navigation between fields
 */
const DigitInput = ({
  value,
  onChange,
  index,
  hasError,
  onComplete,
  disabled,
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    // Auto-focus on first input when component mounts
    if (index === 0 && !disabled) {
      // Add a small delay to ensure the component is fully rendered
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [index, disabled]);

  const handleChange = (event) => {
    const { value: inputValue } = event.target;

    // Only allow digits
    if (!/^\d*$/.test(inputValue)) {
      return;
    }

    // Only allow single digit
    if (inputValue.length > 1) {
      return;
    }

    onChange(index, inputValue);

    // Auto-advance to next input if value entered
    if (inputValue && index < 5) {
      const nextInput = document.querySelector(`input[data-index="${index + 1}"]`);
      if (nextInput) {
        nextInput.focus();
      }
    }

    // Check if all digits are filled
    if (inputValue && index === 5) {
      onComplete();
    }
  };

  const handleKeyDown = (event) => {
    // Handle backspace - move to previous input if current is empty
    if (event.key === 'Backspace' && !value && index > 0) {
      const prevInput = document.querySelector(`input[data-index="${index - 1}"]`);
      if (prevInput) {
        prevInput.focus();
      }
    }

    // Handle arrow keys for navigation
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      const prevInput = document.querySelector(`input[data-index="${index - 1}"]`);
      if (prevInput) {
        prevInput.focus();
      }
    }

    if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      const nextInput = document.querySelector(`input[data-index="${index + 1}"]`);
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text');

    // Check if pasted data is exactly 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      // Distribute digits across all inputs
      Array.from({ length: 6 }, (_, i) => {
        onChange(i, pastedData[i]);
        return null;
      });

      // Focus the last input
      const lastInput = document.querySelector('input[data-index="5"]');
      if (lastInput) {
        lastInput.focus();
      }

      // Trigger completion
      onComplete();
    }
  };

  return (
    <TextField
      ref={inputRef}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      inputProps={{
        'data-index': index,
        maxLength: 1,
        style: {
          textAlign: 'center',
          fontSize: '50px',
          fontWeight: 'bold',
          padding: '12px',
        },
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          '& fieldset': {
            borderColor: hasError ? '#EF456E' : '#DEE5EE',
            borderWidth: '2px',
          },
          '&:hover fieldset': {
            borderColor: hasError ? '#EF456E' : '#2196f3',
          },
          '&.Mui-focused fieldset': {
            borderColor: hasError ? '#EF456E' : '#2196f3',
          },
        },
        width: '70px',
        height: '60px',
      }}
      disabled={disabled}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
    />
  );
};

DigitInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  hasError: PropTypes.bool,
  onComplete: PropTypes.func,
  disabled: PropTypes.bool,
};

DigitInput.defaultProps = {
  hasError: false,
  onComplete: () => {},
  disabled: false,
};

/**
 * DigitInputGroup - Container for 6 digit inputs
 */
const DigitInputGroup = ({
  values,
  onChange,
  hasError,
  onComplete,
  disabled,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(6, 1fr)',
      gap: '20px',
      maxWidth: '400px',
      margin: '20px auto',
      marginLeft: '100px',
      marginTop: '-25px',
    }}
  >
    {Array.from({ length: 6 }, (_, index) => (
      <DigitInput
        key={index}
        value={values[index] || ''}
        onChange={onChange}
        index={index}
        hasError={hasError}
        onComplete={onComplete}
        disabled={disabled}
      />
    ))}
  </Box>
);

DigitInputGroup.propTypes = {
  values: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
  onComplete: PropTypes.func,
  disabled: PropTypes.bool,
};

DigitInputGroup.defaultProps = {
  hasError: false,
  onComplete: () => {},
  disabled: false,
};

export default DigitInputGroup;
